import { useMemo } from 'react'
import { Truck } from 'lucide-react'
import { KanbanBoard, type KanbanItem, type KanbanStage, Chip } from '@/components/ui'
import { usePreferences } from '@/shell/stores/preferences'
import type { PickupSummary, Stage } from '../api/types'
import { PickupActionsMenu } from './PickupActionsMenu'
import {
  BOARD_STAGES,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
  transitionAllowed,
} from '../utils/stage-meta'

const MODULE_ID = 'material-pickup'

interface PickupKanbanItem extends KanbanItem {
  pickup: PickupSummary
}

export interface MaterialPickupKanbanProps {
  items: PickupSummary[]
  selectedId?: string
  onSelect: (pickup: PickupSummary) => void
  onTransition: (pickup: PickupSummary, target: Stage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  if (v === 'info') return 'neutral'
  return v
}

const STAGE_BY_ID: Record<Stage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_SHORT_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<Stage, KanbanStage>,
)

function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as Stage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

export function MaterialPickupKanban({
  items,
  selectedId,
  onSelect,
  onTransition,
}: MaterialPickupKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: PickupKanbanItem[] = useMemo(
    () => items.map((pickup) => ({ id: pickup.id, stageId: pickup.status, pickup })),
    [items],
  )

  return (
    <KanbanBoard<PickupKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) => transitionAllowed(from as Stage, to as Stage)}
      onAdvance={(item, _from, to) => onTransition(item.pickup, to as Stage)}
      onSelect={(item) => onSelect(item.pickup)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <PickupKanbanCard pickup={item.pickup} />}
      scrollRestorationKey="material-pickup"
    />
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function PickupKanbanCard({ pickup }: { pickup: PickupSummary }) {
  return (
    <div
      className={`flex flex-col gap-1.5 ${
        pickup.status === 'needs_info' ? '-ml-3 border-l-2 border-l-warning pl-2.5' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="text-role-data-primary truncate" title={pickup.vendor_name ?? ''}>
          {pickup.vendor_name ?? <em className="text-tertiary">unknown vendor</em>}
        </div>
        <PickupActionsMenu row={pickup} />
      </div>
      {pickup.odoo_po_names.length > 0 ? (
        <div className="text-role-data-secondary truncate">
          {pickup.odoo_po_names.join(', ')}
        </div>
      ) : null}
      {pickup.proposed_pickup_date ? (
        <div className="text-role-metadata">
          Pickup {formatDate(pickup.proposed_pickup_date)}
        </div>
      ) : null}
      {pickup.carrier_name ? (
        <div className="text-role-metadata truncate" title={pickup.carrier_name}>
          {pickup.carrier_name}
        </div>
      ) : null}
      {pickup.extraction_state !== 'complete' ? (
        <Chip
          size="xs"
          variant={pickup.extraction_state === 'failed' ? 'danger' : 'neutral'}
        >
          {pickup.extraction_state === 'pending'
            ? 'Extraction queued'
            : pickup.extraction_state === 'running'
              ? 'Extracting…'
              : 'Extraction failed'}
        </Chip>
      ) : null}
      {pickup.freight_shipment_id != null ? (
        <div className="flex items-center gap-1 text-role-metadata text-brand">
          <Truck size={11} />
          <span>Tracking linked</span>
        </div>
      ) : null}
      {pickup.notes ? (
        <div className="line-clamp-2 text-role-metadata italic" title={pickup.notes}>
          {pickup.notes}
        </div>
      ) : null}
    </div>
  )
}
